import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, filter, firstValueFrom, from, map, of, Subscription, switchMap, take } from 'rxjs';
import { DialogRef, DialogService, IconComponent, ToastContainerComponent } from 'continity-shared-ui';
import { AuthService, ShellComponent, ShellConfigService, ShellProduct } from 'continity-shared-shell';
import { buildPhoenixProducts } from './product-switcher';
import { APP_ROUTES } from './core/constants/app-routes';
import { hasPhoenixAccessRole, isCompanyAdmin } from './core/auth/phoenix-access';
import { ConsentGateDialogComponent } from './legal/consent-gate-dialog.component';
import { PhoenixPrivacyConsentService } from './legal/phoenix-privacy-consent.service';
import { PhoenixIconComponent } from './shared/ui/phoenix-icon.component';
import { PhoenixSignalRService } from './core/realtime/phoenix-signalr.service';

/**
 * Shell-framed app layout (sidebar + header) for the AUTHENTICATED Phoenix areas
 * (board / runs / environments / no-access). Extracted out of {@link AppComponent}
 * so the public landing page can render FULL-BLEED outside the `cs-shell` frame
 * (PBI 1467 — interim marketing landing). The router renders `/board`, `/runs`,
 * `/backup-environments`, `/no-access` into the `<router-outlet>` that lives INSIDE
 * `cs-shell`'s content slot; `/` bypasses this layout entirely (see {@link routes}).
 *
 * This is the SAME `<cs-shell>` composition that previously lived in AppComponent —
 * only the app-specific sidebar nav (board / runs / environments) and its styling
 * stay here, projected into the shell's `sidebar-nav` slot. The standalone
 * installer-download nav entry + page are retired (Environments redesign); installer
 * downloads happen via "Add environment" + the per-env re-issue. No change to
 * `continity-shared-shell` itself.
 */
@Component({
  selector: 'phoenix-shell-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    IconComponent,
    PhoenixIconComponent,
    ShellComponent,
    ToastContainerComponent,
  ],
  template: `
    <!-- Toast host for the authenticated areas. shared-ui's container reads TOAST_SERVICE,
         which this app binds to PhoenixToastService (see app.config.ts). -->
    <app-toast-container></app-toast-container>

    <cs-shell
      appTitle="Continity Phoenix"
      [appDescription]="'phoenix.shell.description' | translate"
      storageKey="phoenix-portal-sidebar"
      [authenticatingText]="'phoenix.shell.authenticating' | translate"
      [themeToggleLabel]="'phoenix.shell.themeToggle' | translate"
      [switchProductLabel]="'phoenix.shell.switchProduct' | translate"
      [logoutLabel]="'phoenix.shell.logout' | translate"
      langCyPrefix="phoenix"
      logoLight="assets/images/phoenix-logo.png"
      logoDark="assets/images/phoenix-logo-dark.png"
      logoCollapsed="assets/images/LogoIndex.png"
      logoAlt="Continity Phoenix"
      logoHref="/board"
      [products]="products">

      <nav #navEl slot="sidebar-nav" class="flex flex-col gap-1 p-2">
        <a class="phoenix-nav-link" [routerLink]="onboardingLink()" routerLinkActive="active"
          data-cy="phoenix-nav-onboarding">
          <phoenix-icon name="rocket" [size]="18"></phoenix-icon>
          <span class="nav-label">{{ 'phoenix.nav.onboarding' | translate }}</span>
        </a>
        <a class="phoenix-nav-link" [routerLink]="['/board']" routerLinkActive="active"
          data-cy="phoenix-nav-board">
          <app-icon name="gauge" [size]="18"></app-icon>
          <span class="nav-label">{{ 'phoenix.nav.board' | translate }}</span>
        </a>
        <a class="phoenix-nav-link" [routerLink]="['/runs']" routerLinkActive="active"
          data-cy="phoenix-nav-runs">
          <app-icon name="list" [size]="18"></app-icon>
          <span class="nav-label">{{ 'phoenix.nav.runs' | translate }}</span>
        </a>
        <a class="phoenix-nav-link" [routerLink]="backupEnvironmentsLink()" routerLinkActive="active"
          data-cy="phoenix-nav-environments">
          <app-icon name="server" [size]="18"></app-icon>
          <span class="nav-label">{{ 'phoenix.nav.environments' | translate }}</span>
        </a>
        @if (canAdministerCompany()) {
          <a class="phoenix-nav-link" [routerLink]="subscriptionLink()" routerLinkActive="active"
            data-cy="phoenix-nav-subscription">
            <app-icon name="subscription" [size]="18"></app-icon>
            <span class="nav-label">{{ 'phoenix.nav.subscription' | translate }}</span>
          </a>
          <a class="phoenix-nav-link" [routerLink]="settingsLink()" routerLinkActive="active"
            data-cy="phoenix-nav-settings">
            <app-icon name="settings" [size]="18"></app-icon>
            <span class="nav-label">{{ 'phoenix.nav.settings' | translate }}</span>
          </a>
        }

        <!-- Legal reachability from the AUTHENTICATED area (imprint / privacy / terms) —
             quiet text links, hidden entirely on the collapsed icon rail (see the
             ResizeObserver below; icon-only legal glyphs would be unreadable noise). The
             labels reuse the shared legal bundles' own titles, so they cannot drift from
             the pages they open. -->
        <div class="phoenix-legal-links mt-6 pt-3 border-t border-gray-300/50 dark:border-gray-600/40 flex flex-col gap-0.5">
          <a class="phoenix-legal-link" [routerLink]="impressumLink()" data-cy="phoenix-nav-impressum">
            {{ 'impressum.title' | translate }}
          </a>
          <a class="phoenix-legal-link" [routerLink]="privacyPolicyLink()" data-cy="phoenix-nav-privacy">
            {{ 'privacyPolicy.title' | translate }}
          </a>
          <a class="phoenix-legal-link" [routerLink]="termsLink()" data-cy="phoenix-nav-terms">
            {{ 'phoenix.legal.termsLink' | translate }}
          </a>
        </div>
      </nav>
    </cs-shell>
  `,
  styles: [
    `
      .phoenix-nav-link {
        position: relative; /* anchor for the active accent bar */
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        color: inherit;
        text-decoration: none;
      }
      .phoenix-nav-link:hover {
        background: rgba(127, 127, 127, 0.12);
      }
      /* Selected entry: left accent bar + orange text/icon — NO fill, NO shadow
         (mirrors the main-portal sidebar-item-active treatment, in Phoenix orange). */
      .phoenix-nav-link.active {
        color: #f97316;
        font-weight: 600;
      }
      .phoenix-nav-link.active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.375rem;
        bottom: 0.375rem;
        width: 3px;
        border-radius: 9999px;
        background: #f97316;
      }
      /* Quiet legal links below the nav — visually subordinate to the app navigation. */
      .phoenix-legal-link {
        padding: 0.25rem 0.75rem;
        font-size: 0.75rem;
        line-height: 1.2;
        color: inherit;
        opacity: 0.65;
        text-decoration: none;
      }
      .phoenix-legal-link:hover {
        opacity: 1;
        text-decoration: underline;
        text-underline-offset: 3px;
      }
    `,
  ],
})
export class ShellLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  /**
   * Cross-portal product switcher entries (922 Entscheidung 8, Option B — ADO #1543):
   * sibling portals from runtime config FIRST, then the current product (Phoenix) LAST,
   * highlighted + non-clickable. Labels are config-driven brand names → NOT i18n'd.
   * Mapping logic lives in the pure {@link buildPhoenixProducts} (unit-tested).
   *
   * Computed ONCE: this component renders only on authenticated routes (after the
   * config-before-auth initializer has resolved, so `portalLinks` is populated), and
   * `portalLinks` is static thereafter. A getter here would rebuild the array on every
   * change-detection pass and churn the `[products]` input identity.
   */
  private readonly shellConfig = inject(ShellConfigService);
  private readonly renderer = inject(Renderer2);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(DialogService);
  private readonly privacyConsent = inject(PhoenixPrivacyConsentService);
  private readonly router = inject(Router);
  private readonly signalR = inject(PhoenixSignalRService);

  readonly products: ShellProduct[] = buildPhoenixProducts(
    this.shellConfig.portalLinks,
    this.auth.currentUser?.authenticationMethod !== 'DevAuth'
  );

  /** Typed sidebar-nav links (no literal route arrays — CLAUDE.md routing rule). */
  readonly onboardingLink = APP_ROUTES.onboarding;
  readonly backupEnvironmentsLink = APP_ROUTES.backupEnvironments;
  readonly settingsLink = APP_ROUTES.settings;
  readonly subscriptionLink = APP_ROUTES.subscription;
  readonly impressumLink = APP_ROUTES.impressum;
  readonly privacyPolicyLink = APP_ROUTES.privacyPolicy;
  readonly termsLink = APP_ROUTES.terms;

  /**
   * Company settings is CompanyAdmin-only (the profile PUT is gated `Tenants.Edit`, which no
   * Phoenix role carries). The route guard already enforces it; hiding the nav entry keeps a
   * Phoenix-role user from finding a door that only leads to /no-access. Defaults to false, so
   * the entry appears once roles resolve rather than flashing for everyone.
   */
  readonly canAdministerCompany = toSignal(
    from(this.auth.waitForAuthInit()).pipe(
      switchMap(() => this.auth.getUserRoles()),
      map(isCompanyAdmin),
      catchError(() => of(false))
    ),
    { initialValue: false }
  );

  @ViewChild('navEl') private navEl?: ElementRef<HTMLElement>;
  private collapseObs?: ResizeObserver;
  private privacyConsentNavigationSubscription?: Subscription;
  private privacyConsentStatusSubscription?: Subscription;
  private consentGateCloseSubscription?: Subscription;
  private consentGateRef?: DialogRef<ConsentGateDialogComponent, boolean>;
  private privacyConsentCheckVersion = 0;
  private destroyed = false;

  /**
   * GDPR/terms consent gate (mirrors the main portal's MainLayout enforcement): the
   * authenticated shell is the single host of every gated route, so checking here covers
   * every entry path (deep link, F5, cross-portal SSO landing) — enforcement deliberately
   * does NOT live on any individual page. The consent record is TENANT-wide and shared with
   * the main portal via `/api/privacy-consent` (see {@link PhoenixPrivacyConsentService} for
   * the architecture rationale), so a tenant that already consented in the BCM portal passes
   * straight through — and vice versa.
   *
   * Unlike the main portal there is NO feature-flag in front of the gate: this app has no
   * feature-flag service, and a Phoenix-ONLY customer would otherwise never see any consent
   * at all (the compliance gap this gate exists to close).
   */
  ngOnInit(): void {
    // The shell persists while switching between its children. Re-checking on completed
    // navigations is what makes /no-access -> a usable page enforce consent again.
    this.privacyConsentNavigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => void this.enforcePrivacyConsent());
    void this.enforcePrivacyConsent();
    void this.startRealtime();
  }

  private async startRealtime(): Promise<void> {
    await this.auth.waitForAuthInit();
    if (this.destroyed || !this.auth.isAuthenticated) return;
    let roles: readonly string[];
    try { roles = await firstValueFrom(this.auth.getUserRoles()); }
    catch (err) { console.warn('[ShellLayout] Phoenix entitlement lookup failed; live updates were not started:', err); return; }
    if (this.destroyed || !hasPhoenixAccessRole(roles)) return;
    try { await this.signalR.start(); }
    catch (err) { console.warn('[ShellLayout] Phoenix live updates unavailable:', err); }
  }

  private async enforcePrivacyConsent(): Promise<void> {
    const checkVersion = ++this.privacyConsentCheckVersion;
    this.privacyConsentStatusSubscription?.unsubscribe();
    if (this.isPrivacyConsentExemptRoute()) {
      this.closeConsentGate();
      return;
    }
    await this.auth.waitForAuthInit();
    if (checkVersion !== this.privacyConsentCheckVersion || !this.auth.isAuthenticated) {
      // Unauthenticated shell visit (e.g. /no-access before login) — nothing to consent to.
      return;
    }
    if (this.isPrivacyConsentExemptRoute()) {
      this.closeConsentGate();
      return;
    }

    this.privacyConsentStatusSubscription = this.privacyConsent.getStatus().pipe(take(1)).subscribe({
      next: (status) => {
        if (
          checkVersion === this.privacyConsentCheckVersion &&
          !this.isPrivacyConsentExemptRoute() &&
          this.privacyConsent.needsConsent(status)
        ) {
          this.openConsentGate();
        }
      },
      error: (err) => {
        if (checkVersion !== this.privacyConsentCheckVersion || this.isPrivacyConsentExemptRoute()) {
          return;
        }
        // Fail-CLOSED, same as the main portal: if the status cannot be read, demand the
        // consent rather than silently letting an un-consented tenant work.
        console.warn('[ShellLayout] Privacy-consent status read failed — showing the gate:', err);
        this.openConsentGate();
      },
    });
  }

  /** Route data, rather than a URL match, makes the restricted-page exception exact and auditable. */
  private isPrivacyConsentExemptRoute(): boolean {
    let activeRoute = this.router.routerState.snapshot.root;
    while (activeRoute.firstChild) activeRoute = activeRoute.firstChild;
    return activeRoute.data['skipPrivacyConsent'] === true;
  }

  private openConsentGate(): void {
    if (this.consentGateRef) return;

    // disableClose: accept or decline (= logout inside the dialog) are the only ways out.
    // autoFocus: false — the DialogService's default focusFirstElement() would land the initial
    // focus on the FIRST focusable element, which is the header's language selector; that reads
    // as if the dialog were about language choice. The shared-ui DialogConfig offers only this
    // boolean (no 'focus the container' option), so the app opts out of auto-focus here; keyboard
    // users tab into the dialog's natural order instead (Kevin E2E finding 2026-08-06).
    const dialogRef = this.dialog.open(ConsentGateDialogComponent, {
      size: 'xl',
      disableClose: true,
      autoFocus: false,
    });
    this.consentGateRef = dialogRef;
    this.consentGateCloseSubscription = dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (this.consentGateRef === dialogRef) this.consentGateRef = undefined;
    });
  }

  private closeConsentGate(): void {
    const dialogRef = this.consentGateRef;
    if (!dialogRef) return;

    this.consentGateRef = undefined;
    this.consentGateCloseSubscription?.unsubscribe();
    this.consentGateCloseSubscription = undefined;
    dialogRef.close();
  }

  /**
   * Icon-only nav labels on the collapsed icon rail — mirrors the ResizeObserver hack already
   * established in `continity-shared-shell`'s `ShellComponent.relocateSwitcher()` (shared-ui's
   * `app-shell` exposes no public `isCollapsed()` signal, so width is the only observable proxy;
   * same <100px rail-vs-expanded threshold). Without this the label text wraps/clips against
   * the narrow rail instead of disappearing. Direct Renderer2 style writes (not a bound property)
   * to match that precedent and avoid extra change-detection churn from a ResizeObserver callback.
   *
   * Selector MUST stay scoped to `.nav-label` (not a bare `span`) — `app-icon`'s own template root
   * is ALSO a `<span [innerHTML]="iconSvg">` (continity-shared-ui), so a bare `span` selector hides
   * the icons too, leaving the rail empty instead of icon-only (caught via screenshot feedback).
   */
  ngAfterViewInit(): void {
    const nav = this.navEl?.nativeElement;
    if (!nav) return;
    this.collapseObs = new ResizeObserver((entries) => {
      const collapsed = entries[0].contentRect.width < 100;
      nav.querySelectorAll<HTMLElement>('.phoenix-nav-link .nav-label').forEach((span) => {
        this.renderer.setStyle(span, 'display', collapsed ? 'none' : '');
      });
      // The legal links are text-only — on the icon rail they would render as clipped
      // fragments, so the whole block disappears with the labels.
      nav.querySelectorAll<HTMLElement>('.phoenix-legal-links').forEach((block) => {
        this.renderer.setStyle(block, 'display', collapsed ? 'none' : '');
      });
    });
    this.collapseObs.observe(nav);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // The auth initializer is not cancellable, so invalidate it before its continuation can read
    // consent status; the HTTP observable itself is cancelled explicitly.
    ++this.privacyConsentCheckVersion;
    this.privacyConsentNavigationSubscription?.unsubscribe();
    this.privacyConsentStatusSubscription?.unsubscribe();
    this.consentGateCloseSubscription?.unsubscribe();
    this.closeConsentGate();
    this.collapseObs?.disconnect();
    void this.signalR.stop();
  }
}
